import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { cx } from "@/lib/utils";

/**
 * Pan/zoom wrapper around the analyzer's SVG preview.
 *
 * Replaces the static ``<img>`` we used in the configurator + cart.
 * Implementation is **DIY** rather than ``react-svg-pan-zoom``:
 *
 * * The underlying source is an ``<img>`` (SVG fetched from R2), so
 *   we don't have direct access to SVG viewport math — we'd be
 *   working with the raster of an SVG either way.
 * * A wrapper ``<div>`` with CSS ``transform: translate() scale()``
 *   does the same job in 80 lines vs. pulling in 30 KB of library.
 * * Wheel = zoom, drag = pan, double-click = reset, two buttons
 *   for accessibility (keyboard zoom + reset).
 *
 * The ratio is preserved — we never deform the part. Zoom range is
 * 0.25× ↔ 8× which covers everything from "fit a 1m × 1m sheet" to
 * "inspect a 0.5mm pierce".
 *
 * The wrapper renders a chrome (title + badge · file name + scale on
 * top; stats line + zoom % + legend on bottom). When ``chrome={false}``
 * it falls back to the raw pan/zoom surface (used inside embedded
 * thumbnails) with a floating mini-toolbar.
 */

export interface DxfPreviewStats {
  widthMm: number;
  heightMm: number;
  holeCount: number;
  totalCutMm: number;
}

interface DxfPreviewProps {
  src: string;
  alt: string;
  className?: string;
  /** Min/max zoom levels. Defaults to 0.25 / 8. */
  minZoom?: number;
  maxZoom?: number;
  /** Render the title row + stats footer + legend. Default ``true``. */
  chrome?: boolean;
  /** File name displayed in the header right-side (chrome only). */
  fileName?: string;
  /** Optional small badge label ("Vektorový obrys") in the header left. */
  badge?: string;
  /** Optional scale label shown next to filename ("1:1"). */
  scaleLabel?: string;
  /** Optional stats line shown in the footer (chrome only). */
  stats?: DxfPreviewStats;
  /**
   * Optional overlay rendered as an absolutely-positioned sibling of
   * the preview ``<img>`` inside the pan/zoom transformed div. The
   * overlay shares the same coordinate space (SVG viewBox 800×600) and
   * the same CSS scale, so user-space coords align with the geometry.
   * Pointer events on the overlay should be disabled by the caller.
   */
  overlay?: ReactNode;
  /**
   * Interaction mode for the surface. ``pan`` is the default — wheel
   * zooms, drag pans. ``measure`` disables pan; clicks are forwarded
   * to whatever the caller renders inside ``overlay`` (which should
   * use pointer-events: auto in that mode).
   */
  mode?: PreviewMode;
  /** Notified when the user clicks one of the mode tabs. Required to render the tabs. */
  onModeChange?: (mode: PreviewMode) => void;
}

export type PreviewMode = "pan" | "measure";

export function DxfPreview({
  src,
  alt,
  className,
  minZoom = 0.25,
  maxZoom = 8,
  chrome = true,
  fileName,
  badge,
  scaleLabel,
  stats,
  overlay,
  mode = "pan",
  onModeChange,
}: DxfPreviewProps): JSX.Element {
  const { t } = useTranslation("common");
  const { t: tQuote } = useTranslation("quote");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);

  const reset = useCallback(() => {
    setScale(1);
    setTx(0);
    setTy(0);
  }, []);

  const setZoom = useCallback(
    (next: number) => {
      const clamped = Math.max(minZoom, Math.min(maxZoom, next));
      setScale(clamped);
    },
    [minZoom, maxZoom],
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    function onWheel(e: WheelEvent) {
      if (e.shiftKey) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setScale((s) => Math.max(minZoom, Math.min(maxZoom, s * (1 + delta))));
    }
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
    };
  }, [minZoom, maxZoom]);

  function onMouseDown(e: React.MouseEvent) {
    if (e.button !== 0) return;
    if (mode !== "pan") return;
    setDragging(true);
    dragOrigin.current = { x: e.clientX, y: e.clientY, tx, ty };
  }

  function onMouseMove(e: React.MouseEvent) {
    if (!dragging || !dragOrigin.current) return;
    setTx(dragOrigin.current.tx + (e.clientX - dragOrigin.current.x));
    setTy(dragOrigin.current.ty + (e.clientY - dragOrigin.current.y));
  }

  function endDrag() {
    setDragging(false);
    dragOrigin.current = null;
  }

  const inMeasure = mode === "measure";

  const surface = (
    <div
      className={cx(
        "relative h-full w-full overflow-hidden bg-surface",
        chrome ? undefined : className,
      )}
      data-testid="dxf-preview"
      style={{
        backgroundImage:
          "linear-gradient(to right, rgba(120,113,108,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(120,113,108,0.08) 1px, transparent 1px)",
        backgroundSize: "14px 14px",
      }}
    >
      <div
        ref={wrapperRef}
        className={cx(
          "relative h-full w-full",
          inMeasure
            ? "cursor-crosshair"
            : dragging
              ? "cursor-grabbing"
              : "cursor-grab",
        )}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onDoubleClick={inMeasure ? undefined : reset}
      >
        <div
          className="relative mx-auto h-full w-full"
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transformOrigin: "center center",
            transition: dragging ? "none" : "transform 60ms linear",
          }}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            className="block h-auto w-full select-none"
          />
          {overlay && (
            <div
              className={cx(
                "absolute inset-0",
                inMeasure ? "pointer-events-auto" : "pointer-events-none",
              )}
            >
              {overlay}
            </div>
          )}
        </div>
      </div>
      {!chrome && (
        // Fallback toolbar so embedded thumbnails (cart) keep their
        // controls — when chrome is on, the floating overlay below owns them.
        <div className="absolute bottom-2 right-2 flex items-center gap-1 rounded-md border border-border bg-surface/90 p-1 shadow-sm backdrop-blur">
          <ZoomBtn
            onClick={() => setZoom(scale / 1.25)}
            label={t("dxfPreview.zoomOut", { defaultValue: "Zoom out" })}
            testId="dxf-preview-zoom-out"
          >
            −
          </ZoomBtn>
          <span className="mono min-w-[3.5rem] text-center text-xs tabular-nums text-ink-3">
            {Math.round(scale * 100)} %
          </span>
          <ZoomBtn
            onClick={() => setZoom(scale * 1.25)}
            label={t("dxfPreview.zoomIn", { defaultValue: "Zoom in" })}
            testId="dxf-preview-zoom-in"
          >
            +
          </ZoomBtn>
          <ZoomBtn
            onClick={reset}
            label={t("dxfPreview.reset", { defaultValue: "Reset" })}
            testId="dxf-preview-reset"
          >
            ⤺
          </ZoomBtn>
        </div>
      )}
    </div>
  );

  if (!chrome) return surface;

  return (
    <div
      className={cx(
        "rounded-md border border-border bg-surface shadow-sm",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-sm font-medium text-ink">
            {tQuote("preview.title")}
          </h3>
          {badge && (
            <span className="rounded border border-border-strong px-1.5 py-0.5 text-[10px] uppercase tracking-[0.08em] text-ink-3">
              {badge}
            </span>
          )}
        </div>
        {(fileName || scaleLabel) && (
          <div className="flex min-w-0 items-center gap-2 text-[11px] text-ink-3">
            {fileName && (
              <span className="mono truncate" title={fileName}>
                {fileName}
              </span>
            )}
            {scaleLabel && (
              <>
                <span aria-hidden="true">·</span>
                <span className="mono tabular-nums">{scaleLabel}</span>
              </>
            )}
          </div>
        )}
      </header>

      <div className="relative aspect-[4/3]">
        {onModeChange && (
          <div className="absolute left-3 top-3 z-10 flex items-center gap-0.5 rounded-md border border-border bg-surface/90 p-0.5 text-[11px] shadow-sm backdrop-blur">
            <ModeBtn
              active={mode === "pan"}
              onClick={() => onModeChange("pan")}
              testId="dxf-preview-mode-pan"
            >
              {tQuote("preview.mode.pan")}
            </ModeBtn>
            <ModeBtn
              active={mode === "measure"}
              onClick={() => onModeChange("measure")}
              testId="dxf-preview-mode-measure"
            >
              {tQuote("preview.mode.measure")}
            </ModeBtn>
          </div>
        )}
        <div className="absolute right-3 top-3 z-10 flex items-center gap-1 rounded-md border border-border bg-surface/90 p-1 text-ink-2 shadow-sm backdrop-blur">
          <ZoomBtn
            onClick={() => setZoom(scale / 1.25)}
            label={t("dxfPreview.zoomOut")}
            testId="dxf-preview-zoom-out"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5 L13.5 13.5" strokeLinecap="round" />
              <path d="M5 7 H9" strokeLinecap="round" />
            </svg>
          </ZoomBtn>
          <ZoomBtn
            onClick={() => setZoom(scale * 1.25)}
            label={t("dxfPreview.zoomIn")}
            testId="dxf-preview-zoom-in"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="7" cy="7" r="4.5" />
              <path d="M10.5 10.5 L13.5 13.5" strokeLinecap="round" />
              <path d="M5 7 H9 M7 5 V9" strokeLinecap="round" />
            </svg>
          </ZoomBtn>
          <ZoomBtn
            onClick={reset}
            label={t("dxfPreview.reset")}
            testId="dxf-preview-reset"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M3 8 a5 5 0 1 0 1.5 -3.5" strokeLinecap="round" />
              <path d="M3 3 V6 H6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </ZoomBtn>
          <a
            href={src}
            download
            aria-label={tQuote("actions.downloadPreview")}
            className="ml-0.5 inline-flex h-7 w-7 items-center justify-center rounded text-ink-2 hover:bg-surface-2"
            data-testid="dxf-preview-download"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M8 2 V10 M5 7 L8 10 L11 7" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 13 H13" strokeLinecap="round" />
            </svg>
          </a>
        </div>
        <div className="h-full w-full">{surface}</div>
      </div>

      <footer className="flex flex-col gap-1.5 border-t border-border px-4 py-2 text-[11px] text-ink-3">
        <div className="flex items-center justify-between gap-3">
          <div className="mono flex flex-wrap items-center gap-x-2 gap-y-0.5 tabular-nums">
            {stats ? (
              <>
                <span data-testid="dxf-preview-stats-dims">
                  {tQuote("preview.stats.dimensions", {
                    width: Math.round(stats.widthMm),
                    height: Math.round(stats.heightMm),
                  })}
                </span>
                <span aria-hidden="true">·</span>
                <span data-testid="dxf-preview-stats-holes">
                  {tQuote("preview.stats.holes", { count: stats.holeCount })}
                </span>
                <span aria-hidden="true">·</span>
                <span data-testid="dxf-preview-stats-cut">
                  {tQuote("preview.stats.cutLength", {
                    length: Math.round(stats.totalCutMm),
                  })}
                </span>
              </>
            ) : (
              <span aria-hidden="true">&nbsp;</span>
            )}
          </div>
          <span
            className="mono tabular-nums text-ink-3"
            data-testid="dxf-preview-zoom-pct"
          >
            {Math.round(scale * 100)} %
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <LegendDot kind="outline" label={tQuote("preview.legendOutline")} />
          <LegendDot kind="hole" label={tQuote("preview.legendHole")} />
          <LegendDot kind="dim" label={tQuote("preview.legendDim")} />
        </div>
      </footer>
    </div>
  );
}

function ZoomBtn({
  onClick,
  label,
  testId,
  children,
}: {
  onClick: () => void;
  label: string;
  testId?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      data-testid={testId}
      className="inline-flex h-7 w-7 items-center justify-center rounded text-ink-2 hover:bg-surface-2"
    >
      {children}
    </button>
  );
}

function ModeBtn({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={cx(
        "rounded px-2 py-1 transition-colors",
        active ? "bg-ink text-surface" : "text-ink-2 hover:bg-surface-2",
      )}
    >
      {children}
    </button>
  );
}

// Source-of-truth colours that mirror what the analyzer's SVG renderer
// emits (see ``packages/analyzer/src/dxf_analyzer/svg.py``) and what the
// FE overlays draw on top of it. Inline so the legend glyphs match the
// rendered geometry exactly even when Tailwind tokens drift.
const LEGEND_INK = "#0f172a"; // svg.py: outer (slate-900)
const LEGEND_HOLE = "#dc2626"; // svg.py: hole (red-600)
const LEGEND_DIM = "#94a3b8"; // DxfDimensionOverlay stroke (slate-400)
const LEGEND_WARN = "#d97706"; // DxfWarningOverlay warn ring (amber-600)

function LegendDot({
  kind,
  label,
}: {
  kind: "outline" | "hole" | "dim" | "problem";
  label: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <LegendGlyph kind={kind} />
      <span>{label}</span>
    </span>
  );
}

/** Mini SVG glyph that matches how the kind is *actually* rendered on
 *  the preview — solid line for stroked contours, ring for hole
 *  circles, dashed line for dimension extension lines. Avoids the
 *  filled-disc "color swatch" that visually misrepresents stroke-only
 *  geometry. */
function LegendGlyph({
  kind,
}: {
  kind: "outline" | "hole" | "dim" | "problem";
}): JSX.Element {
  if (kind === "hole") {
    return (
      <svg
        viewBox="0 0 12 12"
        className="inline-block h-3 w-3"
        aria-hidden="true"
      >
        <circle
          cx="6"
          cy="6"
          r="3.5"
          fill="none"
          stroke={LEGEND_HOLE}
          strokeWidth="1.4"
        />
      </svg>
    );
  }
  if (kind === "problem") {
    return (
      <svg
        viewBox="0 0 12 12"
        className="inline-block h-3 w-3"
        aria-hidden="true"
      >
        <circle
          cx="6"
          cy="6"
          r="3.5"
          fill="none"
          stroke={LEGEND_WARN}
          strokeWidth="1.4"
          strokeDasharray="2 1.5"
        />
      </svg>
    );
  }
  // outline + dim: short horizontal stroke that mirrors the actual
  // line draw (solid for outline, dashed for dim).
  const stroke = kind === "outline" ? LEGEND_INK : LEGEND_DIM;
  const dashed = kind === "dim";
  return (
    <svg
      viewBox="0 0 14 6"
      className="inline-block h-3 w-3.5"
      aria-hidden="true"
    >
      <line
        x1="0.5"
        y1="3"
        x2="13.5"
        y2="3"
        stroke={stroke}
        strokeWidth={kind === "outline" ? 1.6 : 1.2}
        strokeLinecap="round"
        strokeDasharray={dashed ? "2 1.5" : undefined}
      />
    </svg>
  );
}
